import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import {
  Project,
  ProjectColumnTemplateItem,
  ProjectColumnTemplateRequestItem,
  ProjectParticipant,
  ProjectRequest,
  ProjectRole
} from '../models/project.model';
import { Backlog, BacklogRequest } from '../models/backlog.model';

@Injectable({ providedIn: 'root' })
export class GdpProjectService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.baseApiUrl}/gdp`;

  // ── Proyectos ──────────────────────────────────────────────────────────────

  getProjects(): Observable<Project[]> {
    return this.http.get<Project[]>(`${this.apiUrl}/projects`);
  }

  getProject(projectId: number): Observable<Project> {
    return this.http.get<Project>(`${this.apiUrl}/projects/${projectId}`);
  }

  createProject(request: ProjectRequest): Observable<Project> {
    return this.http.post<Project>(`${this.apiUrl}/projects`, request);
  }

  updateProject(projectId: number, request: ProjectRequest): Observable<Project> {
    return this.http.put<Project>(`${this.apiUrl}/projects/${projectId}`, request);
  }

  deleteProject(projectId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/projects/${projectId}`);
  }

  // ── Plantilla de columnas de kanban ────────────────────────────────────────

  /** Plantilla del proyecto ordenada por position; lista vacía = preset por defecto. */
  getColumnTemplate(projectId: number): Observable<ProjectColumnTemplateItem[]> {
    return this.http.get<ProjectColumnTemplateItem[]>(
      `${this.apiUrl}/projects/${projectId}/column-template`
    );
  }

  /** Sustituye la plantilla (position = índice del array). Enviar [] la borra. */
  updateColumnTemplate(
    projectId: number,
    columns: ProjectColumnTemplateRequestItem[]
  ): Observable<ProjectColumnTemplateItem[]> {
    return this.http.put<ProjectColumnTemplateItem[]>(
      `${this.apiUrl}/projects/${projectId}/column-template`,
      columns
    );
  }

  // ── Participantes de proyecto ──────────────────────────────────────────────

  getParticipants(projectId: number): Observable<ProjectParticipant[]> {
    return this.http.get<ProjectParticipant[]>(`${this.apiUrl}/projects/${projectId}/participants`);
  }

  addParticipant(projectId: number, accountId: number, type: ProjectRole): Observable<ProjectParticipant> {
    return this.http.post<ProjectParticipant>(`${this.apiUrl}/projects/${projectId}/participants`, { accountId, type });
  }

  updateParticipant(projectId: number, participantId: number, type: ProjectRole): Observable<ProjectParticipant> {
    return this.http.put<ProjectParticipant>(
      `${this.apiUrl}/projects/${projectId}/participants/${participantId}`, { type }
    );
  }

  removeParticipant(projectId: number, participantId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/projects/${projectId}/participants/${participantId}`);
  }

  // ── Backlogs ───────────────────────────────────────────────────────────────

  getBacklogs(projectId: number): Observable<Backlog[]> {
    return this.http.get<Backlog[]>(`${this.apiUrl}/projects/${projectId}/backlogs`);
  }

  createBacklog(projectId: number, request: BacklogRequest): Observable<Backlog> {
    return this.http.post<Backlog>(`${this.apiUrl}/projects/${projectId}/backlogs`, request);
  }

  getBacklog(backlogId: number): Observable<Backlog> {
    return this.http.get<Backlog>(`${this.apiUrl}/backlogs/${backlogId}`);
  }

  updateBacklog(backlogId: number, request: BacklogRequest): Observable<Backlog> {
    return this.http.put<Backlog>(`${this.apiUrl}/backlogs/${backlogId}`, request);
  }

  /** Asigna prioridad 0..n a las tareas del backlog según el orden recibido. */
  reorderBacklogTasks(backlogId: number, taskIds: number[]): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/backlogs/${backlogId}/tasks/reorder`, { taskIds });
  }

  /** Mueve una tarea a otro backlog del mismo proyecto, insertándola en la posición dada. */
  moveTaskToBacklog(backlogId: number, taskId: number, position: number): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/backlogs/${backlogId}/tasks/${taskId}/move`, { position });
  }

  deleteBacklog(backlogId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/backlogs/${backlogId}`);
  }
}
